import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {
  ContactPermission,
  ContactPermissionMessageType,
  Conversation,
  Message,
  MessageType,
  ShortMessageStatus
} from '../../../models/inbox';
import {BackendHttpService} from '../../core/backend-http.service';
import {apiEndpoints, CommonConstant} from '../../app.constants';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '../user.service';
import {ObjectUtils} from '../../utils/object.utils';
import {User} from '../../../models/user';

@Injectable()
export class CommunicationService extends BackendHttpService<any> {

  constructor( protected router: Router,
               protected translate: TranslateService,
               private userService: UserService) {
    super(router, translate);
  }

  /**
   * retrieve list of communication based on company id
   * @param {number} companyId, {number} offset, {Date} timeStamp
   * @returns {Observable<[{id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string} , {id: number; status: number; sender: string; message: string}]>}
   */
  fetchConservationList(companyId: number, offset: number, timeStamp: Date) {
    return this.get(apiEndpoints.loadConservations.replace(':companyId', companyId.toString()), {
      limit: CommonConstant.DEFAULT_CHAT_MESSAGES_SIZE,
      offset,
      timestamp: timeStamp.getTime()
    }).map(res => res && res.data ? {
      offset: offset + res.data.conversations.length,
      conversations: this.normalizeConversationList(res.data.conversations, companyId)
    } : {
      offset: offset,
      conversations: []
    });
  }

  /**
   * get contact permission data based on token
   * @param permissionToken
   * @returns {Observable<ContactPermission>}
   */
  getPermissionByToken(permissionToken): Observable<ContactPermission> {
    return this.get(`${apiEndpoints.loadContactPermission}?permissionToken=${permissionToken}`).map(res => res && res.data ? ({
      id: res.data.permission_id,
      status: res.data.permission_status,
      influencerId: res.data.influencer_id,
      influencerName: res.data.influencer_name,
      advertiserName: res.data.advertiser_name,
      advertiserId: res.data.advertiser_id,
      agencyName: res.data.agency_name,
      expiresAt: res.data.expires_at
    }) : null);
  }

  private normalizeConversationList(conversationList, companyId) {
    if (this.userService.isAdmin) {
      return conversationList.map(con => this.normalizeConversationData(con, companyId));
    }
    const newConversationList = conversationList.map(con => this.normalizeConversationData(con, companyId)).filter(c => !c.isHidden);
    return newConversationList;
  }

  private normalizeConversationData(conversation: any, companyId: number) {
    let currentParticipant;
    const currentUser = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    currentParticipant = conversation.participants.find(p => p.user && currentUser && p.user.id  === currentUser.data.id);
    let numOfUnseenMessage = 0;
    if (currentParticipant && currentParticipant.lastSeenMessage) {
      const lastSeenTime = currentParticipant.lastSeenMessage.date;
      numOfUnseenMessage = conversation.messages.filter(m => m.date > lastSeenTime && m.participant && m.participant.id !== currentParticipant.id).length;
    }
    return Object.assign({}, conversation, {
      status: numOfUnseenMessage > 0 ? ShortMessageStatus.Warning : ShortMessageStatus.Normal,
      isHidden: currentParticipant && currentParticipant.hidden,
      isJoined: typeof currentParticipant !== 'undefined',
      messages: this.normalizeConversationMessage(conversation, companyId)
    });
  }

  fetchConversationDetails(companyId: number, conversationId: number, pageNo = 0): Observable<Conversation> {
    return this.get(apiEndpoints.getConversationDetails.replace(':companyId', companyId.toString()).replace(':conversationId', conversationId.toString()), {
      limit: CommonConstant.DEFAULT_CHAT_MESSAGES_SIZE,
      offset: pageNo * CommonConstant.DEFAULT_CHAT_MESSAGES_SIZE
    }).map(res => res && res.data ? this.normalizeConversationData(res.data, companyId) : null);
  }

  sendMessage(companyId: number, conversationId: number, content: string): Observable<any> {
    return this.post(apiEndpoints.getConversationDetails.replace(':companyId', companyId.toString()).replace(':conversationId', conversationId.toString()), {
      content,
      version: 1
    }).map(res => res.data);
  }

  fetchNewMessages(companyId: number, conversationId: number, lastActivity: string): Observable<Message[]> {
    const endPoint = apiEndpoints.fetchNewMessages.replace(':companyId', companyId.toString()).replace(':conversationId', conversationId.toString());
    if (!lastActivity) {
      return this.get(endPoint).map(res => res && res.data ? this.normalizeConversationMessage(res.data, companyId) : null);
    } else {
      return this.get(endPoint, {
        lastActivity
      }).map(res => res && res.data ? this.normalizeConversationMessage(res.data, companyId) : null);
    }

  }

  fetchInfluencerConversation(companyId: number, influencerId: number): Observable<Conversation> {
    return this.get(apiEndpoints.fetchInfluencerConversation.replace(':companyId', companyId.toString()).replace(':influencerId', influencerId.toString()))
      .map(res => res && res.data ? this.normalizeConversationData(res.data.conversation, companyId) : null);
  }

  markMessageAsSeen(companyId, conversationId, messageId) {
    return this.post(apiEndpoints.markMessageAsSeen.replace(':companyId', companyId.toString())
      .replace(':conversationId', conversationId.toString()).replace(':messageId', messageId),
      { message_id: messageId }).map(res => res ? res.data : null);
  }

  deleteConversation(companyId, conversationId) {
    return this.delete(apiEndpoints.getConversationDetails.replace(':companyId', companyId.toString())
        .replace(':conversationId', conversationId.toString())).map(res => res ? res.data : null);
  }

  public normalizeConversationName(conversation) {
    const splitConversationName = conversation.name.split(', ');
    const currentInfluencer = this.userService.getInfluencerInfo();
    if (currentInfluencer) {
      return splitConversationName.find(name => name !== currentInfluencer.name);
    } else {
      const currentAdvertiser = this.userService.getCurrentAdvertiser();
      if (currentAdvertiser) {
        return splitConversationName.find(name => name !== currentAdvertiser.name);
      }
      return '';
    }
  }

  private normalizeConversationMessage(conversation, companyId) {
    const orderedMessages = conversation.messages.sort((a, b) => a.date - b.date);
    return orderedMessages.map(msg => ({
      id: msg.id,
      conservationId: conversation.id,
      sender: msg.participant ? msg.participant : null,
      content: msg.content,
      messageType: msg.messageType,
      type: this.getMessageType(msg, companyId),
      recipientName: msg.participant && msg.participant.user ? msg.participant.user.fullName : null,
      sentTime: msg.date,
      campaignId: msg.campaignId,
      campaignInfluencerId: msg.campaignInfluencerId,
      status: msg.campaignStatus ? msg.campaignStatus.toLowerCase() : null,
      activityAuthor: msg.activityAuthor
    }));
  }

  private getMessageType(message, companyId) {
    if (!message.participant ||
      message.messageType === ContactPermissionMessageType.accepted ||
      message.messageType === ContactPermissionMessageType.requested ||
      message.messageType === ContactPermissionMessageType.rejected) {
      return MessageType.Invitation;
    }

    if (message.participant.company.id === companyId) {
      return MessageType.OutGoing;
    }
    return MessageType.InComming;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/communication/communication.service.ts